import {useEffect, useMemo, useRef, useState} from 'react';

import {getOutlookMailboxUserEmail} from '../../../outlookMailboxEmail';

export type OutlookRecipient = {
  name?: string;
  email: string;
  field: 'from' | 'to' | 'cc' | 'bcc';
};

const fieldOrder: OutlookRecipient['field'][] = ['from', 'to', 'cc', 'bcc'];

const normalizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

const asEmailString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const normalizeRecipient = (
  raw: unknown,
  field: OutlookRecipient['field']
): OutlookRecipient | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const rawRecipient = raw as {
    name?: unknown;
    emailAddress?: unknown;
    address?: unknown;
    displayName?: unknown;
  };

  const email =
    asEmailString(rawRecipient.emailAddress) ??
    asEmailString(rawRecipient.address) ??
    null;
  if (!email) {
    return null;
  }

  const name =
    asEmailString(rawRecipient.displayName || rawRecipient.name) ?? undefined;
  return {email, name, field};
};

const dedupe = (recipients: OutlookRecipient[]): OutlookRecipient[] => {
  const seen = new Set<string>();
  const out: OutlookRecipient[] = [];
  for (const rec of recipients) {
    const key = `${rec.field}:${normalizeEmail(rec.email)}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(rec);
  }
  return out;
};

const recipientsKey = (recipients: OutlookRecipient[]): string => {
  return recipients
    .slice()
    .sort((a, b) => {
      const diff = fieldOrder.indexOf(a.field) - fieldOrder.indexOf(b.field);
      if (diff !== 0) {
        return diff;
      }
      return normalizeEmail(a.email).localeCompare(normalizeEmail(b.email));
    })
    .map(
      (rec) =>
        `${rec.field}|${normalizeEmail(rec.email)}|${(rec.name ?? '').trim()}`
    )
    .join(',');
};

const isComposeRecipientsObject = (value: unknown): boolean => {
  return Boolean(
    value && typeof value === 'object' && 'getAsync' in (value as any)
  );
};

const getMailboxItem = (): any | null => {
  if (typeof Office === 'undefined') {
    return null;
  }
  return Office.context?.mailbox?.item ?? null;
};

const isMessageItem = (item: any | null): boolean => {
  return Boolean(
    item && item.itemType === Office.MailboxEnums.ItemType.Message
  );
};

const readRecipientsFromReadModeArray = (
  value: unknown,
  field: OutlookRecipient['field']
): OutlookRecipient[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((rec) => normalizeRecipient(rec, field))
    .filter((rec): rec is OutlookRecipient => rec != null);
};

const readRecipientFromReadModeValue = (
  value: unknown,
  field: OutlookRecipient['field']
): OutlookRecipient[] => {
  const recipient = normalizeRecipient(value, field);
  return recipient ? [recipient] : [];
};

const getAsyncRecipients = (
  recipientsObj: any,
  field: OutlookRecipient['field']
): Promise<OutlookRecipient[]> => {
  if (!recipientsObj || typeof recipientsObj.getAsync !== 'function') {
    return Promise.resolve([]);
  }
  return new Promise((resolve) => {
    recipientsObj.getAsync((result: any) => {
      const value = result?.value;
      resolve(readRecipientsFromReadModeArray(value, field));
    });
  });
};

export const readOutlookMessageRecipients = async (): Promise<
  OutlookRecipient[]
> => {
  const item = getMailboxItem();
  if (!isMessageItem(item)) {
    return [];
  }

  // Read mode: from is an object; to/cc/bcc are arrays.
  if (
    Array.isArray(item.to) ||
    Array.isArray(item.cc) ||
    Array.isArray(item.bcc)
  ) {
    return dedupe([
      ...readRecipientFromReadModeValue(item.from, 'from'),
      ...readRecipientsFromReadModeArray(item.to, 'to'),
      ...readRecipientsFromReadModeArray(item.cc, 'cc'),
      ...readRecipientsFromReadModeArray(item.bcc, 'bcc'),
    ]);
  }

  // Compose mode: to/cc/bcc are Recipients objects w/ getAsync.
  const [to, cc, bcc] = await Promise.all([
    getAsyncRecipients(item.to, 'to'),
    getAsyncRecipients(item.cc, 'cc'),
    getAsyncRecipients(item.bcc, 'bcc'),
  ]);
  return dedupe([...to, ...cc, ...bcc]);
};

export const readOutlookMessageFromAddress = async (): Promise<string> => {
  const item = getMailboxItem();
  if (!isMessageItem(item)) {
    return '';
  }
  const anyItem = item as {
    from?:
      | Office.EmailAddressDetails
      | {
          getAsync?: (
            cb: (r: Office.AsyncResult<Office.EmailAddressDetails>) => void
          ) => void;
        };
  };
  const direct = anyItem.from as Office.EmailAddressDetails | undefined;
  if (direct?.emailAddress && typeof direct.emailAddress === 'string') {
    return direct.emailAddress.trim();
  }
  const asyncFrom = anyItem.from as
    | {
        getAsync?: (
          cb: (r: Office.AsyncResult<Office.EmailAddressDetails>) => void
        ) => void;
      }
    | undefined;
  const getFromAsync = asyncFrom?.getAsync;
  if (asyncFrom && typeof getFromAsync === 'function') {
    return new Promise<string>((resolve) => {
      getFromAsync.call(
        asyncFrom,
        (r: Office.AsyncResult<Office.EmailAddressDetails>) => {
          if (
            r.status === Office.AsyncResultStatus.Succeeded &&
            r.value?.emailAddress
          ) {
            resolve(r.value.emailAddress.trim());
          } else {
            resolve(getOutlookMailboxUserEmail()?.trim() ?? '');
          }
        }
      );
    });
  }
  return getOutlookMailboxUserEmail()?.trim() ?? '';
};

export const readOutlookMessageSubject = async (): Promise<string> => {
  const item = getMailboxItem();
  if (!isMessageItem(item)) {
    return '';
  }
  const anyItem = item as unknown as {
    subject?:
      | string
      | {getAsync: (cb: (r: Office.AsyncResult<string>) => void) => void};
  };
  const subj = anyItem.subject;
  if (typeof subj === 'string') {
    return subj.trim();
  }
  if (
    subj &&
    typeof subj === 'object' &&
    'getAsync' in subj &&
    typeof (subj as {getAsync: unknown}).getAsync === 'function'
  ) {
    const subjectObj = subj as {
      getAsync: (cb: (r: Office.AsyncResult<string>) => void) => void;
    };
    return new Promise<string>((resolve) => {
      subjectObj.getAsync((r: Office.AsyncResult<string>) => {
        if (
          r.status === Office.AsyncResultStatus.Succeeded &&
          typeof r.value === 'string'
        ) {
          resolve(r.value.trim());
        } else {
          resolve('');
        }
      });
    });
  }
  return '';
};

export const useOutlookEmailRecipients = (): {
  recipients: OutlookRecipient[];
  isMessageContext: boolean;
} => {
  const [recipients, setRecipients] = useState<OutlookRecipient[]>([]);
  const [isMessageContext, setIsMessageContext] = useState<boolean>(() => {
    const item = getMailboxItem();
    return isMessageItem(item);
  });
  const lastKeyRef = useRef<string>('');
  const syncRequestRef = useRef(0);
  const focusHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let cancelled = false;

    const sync = async () => {
      const requestId = syncRequestRef.current + 1;
      syncRequestRef.current = requestId;
      const item = getMailboxItem();
      const isMsg = isMessageItem(item);
      if (!cancelled) {
        setIsMessageContext((prev) => (prev === isMsg ? prev : isMsg));
      }
      const next = await readOutlookMessageRecipients();
      if (!cancelled && requestId === syncRequestRef.current) {
        const nextKey = recipientsKey(next);
        if (nextKey !== lastKeyRef.current) {
          lastKeyRef.current = nextKey;
          setRecipients(next);
        }
      }
    };

    void sync();

    const mailbox =
      typeof Office !== 'undefined' ? Office.context?.mailbox : undefined;
    const item = getMailboxItem();
    const shouldPoll =
      Boolean(item) &&
      isMessageItem(item) &&
      (isComposeRecipientsObject(item.to) ||
        isComposeRecipientsObject(item.cc) ||
        isComposeRecipientsObject(item.bcc));

    // Selection changes (pinned task pane).
    if (mailbox?.addHandlerAsync) {
      mailbox.addHandlerAsync(
        Office.EventType.ItemChanged,
        () => void sync(),
        () => {}
      );
    }

    // Best-effort recipient-change event (not consistently available across hosts/types).
    const recipientsEventType: any =
      (typeof Office !== 'undefined' &&
        (Office.EventType as any)?.RecipientsChanged) ||
      'recipientsChanged';

    if (item?.addHandlerAsync) {
      try {
        item.addHandlerAsync(
          recipientsEventType,
          () => void sync(),
          () => {}
        );
      } catch {
        // ignore
      }
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void sync();
      }
    };
    const onFocus = () => void sync();
    focusHandlerRef.current = onFocus;
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    // Fallback polling (compose-mode editing is otherwise hard to track reliably).
    const interval = shouldPoll
      ? window.setInterval(() => {
          void sync();
        }, 1500)
      : null;

    return () => {
      cancelled = true;
      if (interval != null) {
        window.clearInterval(interval);
      }
      document.removeEventListener('visibilitychange', onVisible);
      if (focusHandlerRef.current) {
        window.removeEventListener('focus', focusHandlerRef.current);
      }
      // We intentionally don't remove handlers since Office APIs differ by host and
      // removing by handler reference is inconsistent across objects. The add-in taskpane
      // lifetime is short and these handlers are scoped to it.
    };
  }, []);

  const sorted = useMemo(() => {
    return [...recipients].sort((a, b) => {
      const d = fieldOrder.indexOf(a.field) - fieldOrder.indexOf(b.field);
      if (d !== 0) {
        return d;
      }
      return normalizeEmail(a.email).localeCompare(normalizeEmail(b.email));
    });
  }, [recipients]);

  return {recipients: sorted, isMessageContext};
};
